import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"
import type {
  ArcadiaGreenButtonCsvCommitResultDto,
  ArcadiaGreenButtonCsvPreviewResultDto,
  ArcadiaGreenButtonDiagnosticDto,
  ArcadiaGreenButtonRecoveryResultDto,
} from "~/api/dto/arcadia-green-button.dto"
import { arcadiaGreenButtonService } from "~/api/services/arcadia-green-button.service"

type RecoverSupportConnectionParams = {
  connectionId: string
  requestId: string
  siteId: string
}

type PreviewArcadiaGreenButtonCsvRecoveryParams = {
  connectionId: string
  file: File
  requestId: string
  siteId: string
}

type CommitArcadiaGreenButtonCsvRecoveryParams = {
  connectionId: string
  expectedContentHash: string
  expectedEvidenceHash: string
  file: File
  requestId: string
  siteId: string
}

export const useArcadiaGreenButtonSupportDiagnostic = (
  siteId: string,
  connectionId: string,
  enabled: boolean,
): UseQueryResult<ArcadiaGreenButtonDiagnosticDto, unknown> =>
  useQuery({
    queryKey: queryKeys.arcadiaGreenButton.diagnostic(siteId, connectionId),
    queryFn: () =>
      arcadiaGreenButtonService.fetchSupportDiagnostic(siteId, connectionId),
    enabled,
    refetchInterval: (data) =>
      data && data.stage !== `complete` && data.stage !== `terminal`
        ? 5_000
        : false,
  })

export const useRecoverArcadiaGreenButtonSupportConnection =
  (): UseMutationResult<
    ArcadiaGreenButtonRecoveryResultDto,
    unknown,
    RecoverSupportConnectionParams,
    unknown
  > => {
    const queryClient = useQueryClient()

    return useMutation({
      mutationFn: ({ connectionId, requestId, siteId }) =>
        arcadiaGreenButtonService.recoverSupportConnection(
          siteId,
          connectionId,
          requestId,
        ),
      onSuccess: (_result, { connectionId, siteId }) => {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.arcadiaGreenButton.diagnostic(
            siteId,
            connectionId,
          ),
        })
        void queryClient.invalidateQueries({
          queryKey: queryKeys.arcadiaGreenButton.site(siteId),
        })
      },
    })
  }

export const usePreviewArcadiaGreenButtonCsvRecovery = (): UseMutationResult<
  ArcadiaGreenButtonCsvPreviewResultDto,
  unknown,
  PreviewArcadiaGreenButtonCsvRecoveryParams,
  unknown
> =>
  useMutation({
    mutationFn: ({ connectionId, file, requestId, siteId }) =>
      arcadiaGreenButtonService.previewCsvRecovery(
        siteId,
        connectionId,
        file,
        requestId,
      ),
  })

/**
 * Commits a previously previewed CSV as a recovery import. This never
 * applies the import to the Proposal — the existing review flow handles
 * that separately, matching every other Green Button import.
 */
export const useCommitArcadiaGreenButtonCsvRecovery = (): UseMutationResult<
  ArcadiaGreenButtonCsvCommitResultDto,
  unknown,
  CommitArcadiaGreenButtonCsvRecoveryParams,
  unknown
> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      connectionId,
      expectedContentHash,
      expectedEvidenceHash,
      file,
      requestId,
      siteId,
    }) =>
      arcadiaGreenButtonService.commitCsvRecovery(siteId, connectionId, file, {
        expectedContentHash,
        expectedEvidenceHash,
        requestId,
      }),
    onSuccess: (result, { connectionId, siteId }) => {
      if (!result.ok) {
        return
      }

      void queryClient.invalidateQueries({
        queryKey: queryKeys.arcadiaGreenButton.diagnostic(siteId, connectionId),
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.arcadiaGreenButton.site(siteId),
      })
    },
  })
}
